import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "videoileo_install_dismissed";

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    if (hidden || isStandalone()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIos()) {
      const t = setTimeout(() => setShowIosTip(true), 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onPrompt);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [hidden]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setHidden(true);
    setDeferred(null);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (hidden || isStandalone() || (!deferred && !showIosTip)) return null;

  return (
    <div
      data-testid="install-prompt"
      className="md:hidden fixed bottom-[calc(3.25rem+env(safe-area-inset-bottom))] inset-x-3 z-50 rounded-md border border-white/15 bg-[#121212]/95 backdrop-blur-xl p-3 flex items-center gap-3 shadow-[0_8px_30px_rgba(0,0,0,0.6)]"
    >
      <img src="/icon-192.png" alt="Videoileo" className="h-9 w-9 rounded-md shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-tight">Add Videoileo to your home screen</p>
        {deferred ? (
          <p className="text-xs text-zinc-500 mt-0.5">Check your schedule & streak in one tap.</p>
        ) : (
          <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1 flex-wrap">
            Tap <Share className="h-3.5 w-3.5 inline text-blue-400" /> then "Add to Home Screen"
          </p>
        )}
      </div>
      {deferred && (
        <button
          onClick={install}
          data-testid="install-prompt-btn"
          className="shrink-0 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-md text-xs font-semibold transition-colors"
        >
          <Download className="h-3.5 w-3.5" /> Install
        </button>
      )}
      <button onClick={dismiss} data-testid="install-prompt-dismiss" className="shrink-0 p-1.5 text-zinc-500 hover:text-white transition-colors">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
